/**
 * SubscribeToExclusiveContentLiveChangesUseCase
 *
 * Starts a Firestore `onSnapshot` listener on
 * `users/{currentUserFirebaseId}/subscriptions where meSubscriber == true` and
 * mirrors each document change into SecureStorage under the
 * `EXCLUSIVE_SUBSCRIPTIONS` prefix:
 *   - added/modified with status === inactive → remove the local entry
 *   - added/modified with any other status    → write the local entry
 *   - removed                                  → remove the local entry
 *
 * Why: when a subscribe / unsubscribe / approval cloud-function call lands,
 * the server writes the subscription document. Without this listener, the web
 * client only sees that change after a full sync — the SecureStorage prefix
 * listener (`ExclusiveContentListener`) fires only on local writes, so the
 * Redux slice and any UI selectors built on it (e.g. `selectHasActiveSubscription`)
 * stay stale until the next app load. This use case closes the loop by being
 * the source of those local writes.
 *
 * Where it runs: started exactly once, at app boot, by
 * `ExclusiveContentListener` in `presentation/components/app/` when the user
 * is authenticated and `firebaseUserId` is known. The component owns the
 * returned `Unsubscribe` and tears it down on sign-out / unmount. It is NOT
 * meant to be called from feature hooks or pages.
 *
 * Flutter parity: mirrors `ExclusiveContentService.listenToExclusiveContentChanges`
 * (`lib/services/exclusive_content_service.dart:13-62`), which Flutter starts at
 * app boot for the same reason.
 *
 * Architecture: thin wrapper over
 * `ExclusiveContentSubscriptionActionsService.listenToExclusiveContentChanges`,
 * which holds the per-change SecureStorage logic. Kept as a use case so the
 * presentation layer never reaches into a domain service directly, matching
 * `subscribeToUserMediaLiveChangesUseCase`.
 */

import { ExclusiveContentSubscriptionActionsService } from '@/domain/services/exclusiveContentSubscription/exclusiveContentSubscriptionActionsService';
import type { Unsubscribe } from 'firebase/firestore';

export class SubscribeToExclusiveContentLiveChangesUseCase {
  constructor(private service = ExclusiveContentSubscriptionActionsService.getInstance()) {}

  execute(currentUserFirebaseId: string): Unsubscribe {
    return this.service.listenToExclusiveContentChanges(currentUserFirebaseId);
  }
}

export const subscribeToExclusiveContentLiveChangesUseCase = new SubscribeToExclusiveContentLiveChangesUseCase();
